import * as React from "react"
import { Col, Row } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import H3 from "../../atoms/typography/H3"
import Subtitle from "../../atoms/typography/Subtitle"
import TextInput from "../TextInput"
import { useCreateOrgJoinRequest } from "../../../hooks/useCreateOrgJoinRequest"
import { checkEmailValid } from "../../../utils/helpers"
import { theme } from "../../theme"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import styled from "styled-components"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import RadioButton from "../../atoms/inputs/RadioButton"

import { useOrganizationGroupsById } from "../../../hooks/useOrganizationGroupsById"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { ApolloError } from "@apollo/client"
import Body from "../../atoms/typography/Body"

interface Props {
  orgName: string
  orgId: number
}

const SelectInitialGroupComponent = styled.div<{ selected: boolean }>`
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: 0.5rem;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  border: ${(props) => (props.selected ? `1px solid ${props.theme.colors.greenDark}` : undefined)};

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }
`

const RadioSelectSection = styled.div`
  display: flex;
  align-items: center;
`

const InviteMembertoSuborgModalContent: React.FunctionComponent<Props> = ({ orgName, orgId }) => {
  const { organization } = useOrganizationGroupsById(orgId)

  const [newEmail, setNewEmail] = React.useState<string>("")
  const [successfullyInvited, setSuccessfullyInvited] = React.useState(false)
  const [inviteErrors, setInviteErrors] = React.useState<Array<string>>([])

  const organizationGroups = organization?.groups?.filter((group: any) => group.__typename === "OrganizationGroupType")

  const adminGroup = organizationGroups && organizationGroups[0]

  const [selectedInitialGroup, setSelectedInitialGroup] = React.useState<string | number>(adminGroup?.id)
  const [selectedRoleId, setSelectedRoleId] = React.useState<string | number>(adminGroup?.roles[0].id)
  const organizationGroupsWithRoles = organizationGroups?.map((group: any) => ({
    id: group.id,
    name: group.name,
    roles: group.roles,
  }))

  React.useMemo(() => {
    setSelectedInitialGroup(adminGroup?.id)
    setSelectedRoleId(adminGroup?.roles[1].id)
  }, [adminGroup])

  const onCompletedCreateJoinRequest = (data: any) => {
    setNewEmail("")
    if (data?.createOrganizationJoinRequest?.errors?.length > 0) {
      setInviteErrors(data?.createOrganizationJoinRequest?.errors[0].messages)
      setTimeout(() => setInviteErrors([]), 2000)
    } else {
      setSuccessfullyInvited(true)
      setTimeout(() => setSuccessfullyInvited(false), 2000)
    }
  }
  const onErrorCreateOrganizationJoinRequest = (error: ApolloError) => {
    captureExceptionWithContext(error, "error creating organization join request")
  }
  const { createOrgJoinRequest } = useCreateOrgJoinRequest({
    onCompleted: (data: any) => onCompletedCreateJoinRequest(data),
    onError: onErrorCreateOrganizationJoinRequest,
  })
  const onCreateOrgJoinRequest = async () => {
    try {
      await createOrgJoinRequest({
        variables: {
          input: {
            toUser: newEmail,
            organization: orgId,
            initialGroup: selectedInitialGroup ? selectedRoleId : undefined,
          },
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error creating organization join request")
    }
  }

  const onClickSendInvite = () => {
    if (checkEmailValid(newEmail ?? "")) {
      onCreateOrgJoinRequest()
    } else {
      setNewEmail("")
      setInviteErrors(["Invalid email address"])
      setTimeout(() => setInviteErrors([]), 2000)
    }
  }

  const onClickSelectInitialGroup = (id: number | string, initialRoleId: number | string) => {
    if (selectedInitialGroup === id) {
      setSelectedInitialGroup(adminGroup?.id)
      setSelectedRoleId(adminGroup?.roles[1].id)
    } else {
      setSelectedInitialGroup(id)
      setSelectedRoleId(initialRoleId)
    }
  }

  return (
    <Col md={8}>
      <Subtitle>Invite a new member to the organization {orgName}</Subtitle>
      <br />
      <H3>New members get an invite through email and has to accept before being added to a group or organizaton.</H3>
      <br />
      <br />
      <TextInput
        textFieldTitle="Email"
        placeholder="Email address of the person you want to invite"
        onChange={(e) => setNewEmail(e.target.value)}
        value={newEmail}
      />
      <HorizontalSpacer spacing={2} />
      <H3>Select initial group: </H3>
      <HorizontalSpacer spacing={1} />
      <Row justify="between">
        {organizationGroupsWithRoles?.map(
          (group: { id: number | string; name: string; roles: Array<{ id: number | string; name: string }> }) => (
            <Col md={4} xs={6} key={group.id}>
              <SelectInitialGroupComponent selected={selectedInitialGroup === group.id}>
                <H3 onClick={() => onClickSelectInitialGroup(group.id, group.roles[1].id)}>{group.name}</H3>
                <HorizontalSpacer spacing={1} />
                <RadioSelectSection>
                  <RadioButton
                    checked={selectedRoleId === group.roles[1].id}
                    onChange={() => setSelectedRoleId(group.roles[1].id)}
                  />
                  <Spacer spacing={1} /> <Body>Viewer</Body>
                </RadioSelectSection>
                <RadioSelectSection>
                  <RadioButton
                    checked={selectedRoleId === group.roles[0].id}
                    onChange={() => setSelectedRoleId(group.roles[0].id)}
                  />
                  <Spacer spacing={1} /> <Body>Admin</Body>
                </RadioSelectSection>
              </SelectInitialGroupComponent>
            </Col>
          ),
        )}
      </Row>
      <HorizontalSpacer spacing={2} />
      <SecondaryButton title="Send invite" onClick={onClickSendInvite} disabled={!newEmail || successfullyInvited} />
      {inviteErrors && (
        <div>
          <HorizontalSpacer spacing={1} />
          <H3 color={theme.colors.red}>{inviteErrors.toString()}</H3>
        </div>
      )}
      {successfullyInvited && <H3 color={theme.colors.greenDark}>Invite sent</H3>}
    </Col>
  )
}

export default InviteMembertoSuborgModalContent
